import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { isAxiosError } from "axios"
import { HttpStatusCodes } from "@/lib/http-status-codes"
import { toast } from "sonner"
import { useCurrentOrganization } from "@/modules/organizations/hooks/use-organizations"
import { folderPermissionService } from "../infrastructure/folder-permission-service"
import { GrantFolderPermissionRequest } from "../dtos/FolderPermissionDto"
import { BulkShareRequest } from "../dtos/BulkPermissionDtos"

export function useFolderPermissions(folderId: string) {
  const org = useCurrentOrganization()
  const orgId = org?.id ?? ""

  return useQuery({
    queryKey: ["folder-permissions", orgId, folderId],
    queryFn: () =>
      folderPermissionService.getFolderPermissions(orgId, folderId),
    enabled: !!orgId && !!folderId,
  })
}

export function useGrantFolderPermission(folderId: string) {
  const queryClient = useQueryClient()
  const org = useCurrentOrganization()
  const orgId = org?.id ?? ""

  return useMutation({
    mutationFn: (request: GrantFolderPermissionRequest) =>
      folderPermissionService.grantFolderPermission(orgId, folderId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["folder-permissions", orgId, folderId],
      })
      toast.success("Permission granted")
    },
    onError: (error) => {
      if (isAxiosError(error)) {
        const status = error.response?.status
        if (status === HttpStatusCodes.BAD_REQUEST) {
          toast.error(
            "Invalid request: provide either a user or a role, not both",
          )
        } else if (status === HttpStatusCodes.FORBIDDEN) {
          toast.error("Access denied")
        } else {
          toast.error("Failed to grant permission")
        }
      } else {
        toast.error("Failed to grant permission")
      }
    },
  })
}

export function useRevokeFolderPermission(folderId: string) {
  const queryClient = useQueryClient()
  const org = useCurrentOrganization()
  const orgId = org?.id ?? ""

  return useMutation({
    mutationFn: (permissionId: string) =>
      folderPermissionService.revokeFolderPermission(orgId, permissionId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["folder-permissions", orgId, folderId],
      })
      toast.success("Permission revoked")
    },
    onError: (error) => {
      if (isAxiosError(error) && error.response?.status === HttpStatusCodes.NOT_FOUND) {
        toast.error("Permission not found")
      } else {
        toast.error("Failed to revoke permission")
      }
    },
  })
}

export function useBulkGrantFolderPermissions(folderId: string) {
  const queryClient = useQueryClient()
  const org = useCurrentOrganization()
  const orgId = org?.id ?? ""

  return useMutation({
    mutationFn: (request: BulkShareRequest) =>
      folderPermissionService.bulkGrantFolderPermissions(
        orgId,
        folderId,
        request,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["folder-permissions", orgId, folderId],
      })
    },
    onError: () => {
      toast.error("Bulk grant failed")
    },
  })
}
